import {OkDocument} from "../../resources/common"
import {BusinessProductApplication} from "../../resources/productApplication"
import {useNavigate} from "react-router-dom"
import React, {useState} from "react"
import {useModal} from "react-modal-hook"
import {ApplicationResubmitForms} from "../ReevaluateApplication/ReevaluatePage"
import ReevaluateBeneficialOwnerForm, {
	ReevaluatedBeneficialOwnerApplicationData,
} from "../../components/ReevaluateApplication/Forms/ReevaluateBeneficialOwnerForm/ReevaluateBeneficialOwnerForm"
import ReevaluateProductApplicationBeneficialOwnerConfirmationModal from "../../components/ReevaluateProductApplication/ReevaluateProductApplicationConfirmationModal/ReevaluateProductApplicationBeneficialOwnerConfirmationModal"
import {BeneficialOwnerResource} from "../../resources/beneficialOwner"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import ProductApplicationDetails from "./ProductApplicationDetails"
import {identificationFieldBeforeUpdateRequest} from "../ReevaluateApplication/ApplicationDetails"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateProductApplicationBeneficialOwnerPageProps {
	application: OkDocument<BusinessProductApplication>
	beneficialOwnerResource: BeneficialOwnerResource
}

function toBeneficialOwnerFormData(
	beneficialOwnerResource?: BeneficialOwnerResource
): ReevaluatedBeneficialOwnerApplicationData | undefined {
	if (
		!beneficialOwnerResource ||
		!beneficialOwnerResource.attributes.address ||
		!beneficialOwnerResource.attributes.dateOfBirth ||
		!beneficialOwnerResource.attributes.email ||
		!beneficialOwnerResource.attributes.phone
	) {
		return undefined
	}

	return {
		fullName: beneficialOwnerResource.attributes.fullName,
		address: beneficialOwnerResource.attributes.address,
		ssn: beneficialOwnerResource.attributes.ssn ?? undefined,
		nationality: beneficialOwnerResource.attributes.nationality ?? undefined,
		passport: beneficialOwnerResource.attributes.passport ?? undefined,
		matriculaConsular: beneficialOwnerResource.attributes.matriculaConsular ?? undefined,
		dateOfBirth: beneficialOwnerResource.attributes.dateOfBirth,
		email: beneficialOwnerResource.attributes.email ?? undefined,
		phone: beneficialOwnerResource.attributes.phone,
		percentage: beneficialOwnerResource.attributes.percentage,
		annualIncome: beneficialOwnerResource.attributes.annualIncome ?? undefined,
		occupation: beneficialOwnerResource.attributes.occupation ?? undefined,
		sourceOfIncome: beneficialOwnerResource.attributes.sourceOfIncome ?? undefined,
	}
}

export default function ReevaluateProductApplicationBeneficialOwnerContainer({
	application,
	beneficialOwnerResource,
}: ReevaluateProductApplicationBeneficialOwnerPageProps) {
	const navigate = useNavigate()
	const beneficialOwnerFormData = toBeneficialOwnerFormData(beneficialOwnerResource)

	const [beneficialOwnerReevaluatedData, setBeneficialOwnerReevaluatedData] = useState<
		ReevaluatedBeneficialOwnerApplicationData | undefined
	>(beneficialOwnerFormData)
	const [conversionError, setConversionError] = useState(false)

	const [showBeneficialOwnerConfirmationModal, closeBeneficialOwnerConfirmationModal] = useModal(() => {
		if (!beneficialOwnerReevaluatedData) {
			setConversionError(true)
			return <></>
		}
		return (
			<ReevaluateProductApplicationBeneficialOwnerConfirmationModal
				onSuccess={() => navigate(`/product-applications/${application.data.id}`)}
				close={closeBeneficialOwnerConfirmationModal}
				productApplicationId={application.data.id}
				beneficialOwnerId={beneficialOwnerResource.id}
				reevaluatedData={beneficialOwnerReevaluatedData}
			/>
		)
	}, [beneficialOwnerReevaluatedData])

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]

	const form = (
		<ProductApplicationDetails
			title={title}
			meta={meta}
			productApplicationId={application.data.id}
			status={application.data.attributes.status}
			formId={ApplicationResubmitForms.beneficialOwnerForm}
		>
			{beneficialOwnerFormData && (
				<ReevaluateBeneficialOwnerForm
					beneficialOwner={beneficialOwnerFormData}
					submitCallback={(values) => {
						const currentValues = {
							ssn: beneficialOwnerResource.attributes.ssn,
							passport: beneficialOwnerResource.attributes.passport,
							matriculaConsular: beneficialOwnerResource.attributes.matriculaConsular,
						}
						const identificationFields = identificationFieldBeforeUpdateRequest({
							currentValues: currentValues,
							updatedValues: values,
						})

						setBeneficialOwnerReevaluatedData({
							...values,
							...identificationFields,
						})
						showBeneficialOwnerConfirmationModal()
					}}
					formId={ApplicationResubmitForms.beneficialOwnerForm}
					disableSensitiveFields
				/>
			)}
		</ProductApplicationDetails>
	)

	if (conversionError) {
		return (
			<Admonition
				type="is-danger"
				title="Failed to process resubmission data, please reach administrator."
				message={""}
			/>
		)
	}

	if (!beneficialOwnerFormData) {
		return (
			<Admonition
				type="is-danger"
				title="Could not be able to show resubmit form, please reach administator."
				message={""}
			/>
		)
	}

	return form
}
